/**
 * Hooks de los límites de formato del pliego y del informe de páginas
 * (feature "Límites de formato").
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";
import type { components } from "@/lib/api/generated";

export type FormatConstraint = components["schemas"]["FormatConstraintResponse"];
export type FormatConstraintUpdate =
  components["schemas"]["FormatConstraintUpdate"];
export type PageReport = components["schemas"]["PageReportResponse"];
export type SectionPageInfo = components["schemas"]["SectionPageInfo"];
export type TrimPlan = components["schemas"]["TrimPlanResponse"];
export type TrimSuggestion = components["schemas"]["TrimSectionSuggestion"];

/**
 * Estimación rápida de páginas por número de palabras, calibrada al tamaño de
 * letra e interlineado exigidos (misma fórmula que la estimación del backend).
 * Es orientativa; el número exacto es el del render/PDF.
 */
export function estimatePagesFromWords(
  wordCount: number,
  opts?: { font_size_pt?: number | null; line_spacing?: number | null }
): number {
  const size = opts?.font_size_pt || 11;
  const spacing = opts?.line_spacing || 1.15;
  const wpp = Math.max(120, 500 * (11 / size) * (1.15 / spacing));
  return Math.max(1, Math.round(wordCount / wpp));
}

/** Límites de formato de una licitación (máx. páginas, fuente, tamaño…). */
export function useFormatConstraints(licitacionId: string | undefined) {
  return useQuery<FormatConstraint, Error>({
    queryKey: ["format-constraints", licitacionId],
    queryFn: () =>
      fetchWithAuth(`/api/v1/licitaciones/${licitacionId}/format-constraints`),
    enabled: !!licitacionId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Extrae los límites del pliego con IA (persiste como 'ia'). */
export function useSuggestFormatConstraints(licitacionId: string) {
  const queryClient = useQueryClient();
  return useMutation<FormatConstraint, Error, void>({
    mutationFn: () =>
      fetchWithAuth(
        `/api/v1/licitaciones/${licitacionId}/format-constraints/suggest`,
        { method: "POST", body: JSON.stringify({}) }
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(["format-constraints", licitacionId], data);
      showToast.success(
        data.notice || "Límites de formato extraídos del pliego"
      );
    },
    onError: (e) => showToast.error(e.message || "Error al extraer los límites"),
  });
}

/** Guarda los límites a mano (source='manual'). */
export function useUpdateFormatConstraints(licitacionId: string) {
  const queryClient = useQueryClient();
  return useMutation<FormatConstraint, Error, FormatConstraintUpdate>({
    mutationFn: (data) =>
      fetchWithAuth(`/api/v1/licitaciones/${licitacionId}/format-constraints`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["format-constraints", licitacionId], data);
      showToast.success("Límites de formato guardados");
    },
    onError: (e) => showToast.error(e.message || "Error al guardar los límites"),
  });
}

/**
 * Informe de páginas de una propuesta vs los límites. Es costoso (renderiza el
 * PDF real en el servidor), así que se lanza bajo demanda con una mutación.
 */
export function usePageReport(proposalId: string) {
  return useMutation<PageReport, Error, void>({
    mutationFn: () =>
      fetchWithAuth(`/api/v1/proposals/${proposalId}/page-report`),
    onError: (e) =>
      showToast.error(e.message || "Error al calcular el número de páginas"),
  });
}

/**
 * Plan de recorte con IA para caber en el límite: qué secciones condensar y a
 * cuántas palabras. Costoso (LLM), se lanza bajo demanda con una mutación.
 */
export function useTrimPlan(proposalId: string) {
  return useMutation<TrimPlan, Error, void>({
    mutationFn: () =>
      fetchWithAuth(`/api/v1/proposals/${proposalId}/trim-plan`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onError: (e) =>
      showToast.error(e.message || "Error al calcular el plan de recorte"),
  });
}
